import { createElement, Children, Fragment, ReactNode } from 'react';

/**
 * Check if a ReactNode has at least one child.
 *
 * If node is undefined/false/null, then returns false
 * If node is a string, then returns false.
 * If 'node' is more than one element, then returns false.
 */
const hasChildren = (node: ReactNode): boolean => {
  return !!safeCountChildren(node);
};

/**
 * The same as React.Children.count, except it can handle string|null|false|undefined
 *
 * If node is string|null|false|undefined, then returns 0
 */
const safeCountChildren = (node: ReactNode): number => {
  if (!node || typeof node === 'string') return 0;

  const child = Children.only(node);

  if (!child) return 0;

  return Children.count((child as JSX.Element).props.children);
};

/**
 * Extract children inside a node, or if it has no children, convert the node into an array.
 *
 * If the node is undefined, then return undefined.
 * If the node is a string, then it will turn it into a fragment.
 * If the 'node' is actually an array of nodes, then return that array of nodes.
 */
export const convertNodeToArray = (node: ReactNode): ReactNode => {
  if (!node) return undefined;

  if (typeof node === 'string')
    return createElement(Fragment, { children: node });

  if (Children.count(node) > 1) return node;

  if (hasChildren(node)) {
    return Children.toArray((node as JSX.Element).props.children);
  }

  return node;
};
